import { useEffect, useMemo, useState, useCallback } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { formattedDate, cleanString } from "../utils";
import {
  useFetchLegacyReport,
  useSearchLegacyReports,
} from "../api/ReportsApi";
import Table from "./Table";
import {
  LegacyReportSummaryItem,
  ReportSummaryItemApi,
  LegacyReportRequest,
} from "../api/Reports";

import { LoadedSince } from "../shared-components/LoadedSince";

import { genericErrorLoadingMessage } from "./utils";
import { useGetActorInfo } from "../api/UsersApi";
import { useCompaniesFetchAll } from "../api/CompaniesApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import DropdownCombo from "../shared-components/DropdownCombo";

import ReactTooltip from "react-tooltip";
import LegacyDonutChart from "./helper-components/LegacyDonut";

import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";

const ReportsLegacyCoreReport = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const location: Location = useLocation();
  const locationState = location.state as {
    fromCompanyUUID?: string;
  };
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [companyUUID, setCompanyUUID] = useState<string | null>(null);
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [reports, setReports] = useState<ReportRowItem<ReportSummaryItemApi>[]>(
    []
  );
  const isSearchActive = Boolean(filter);
  const SearchName = searchParams.get("search");
  const pageSize = 10;

  const {
    mutate: getActorInfo,
    data: actorInfo,
    isLoading: isLoadingActorInfo,
  } = useGetActorInfo();

  const {
    mutate: getAllCompanies,
    data: companiesInfo,
    isLoading: isLoadingCompanies,
  } = useCompaniesFetchAll();

  const {
    isLoading: loadingReports,
    data: reportsData,
    mutateAsync: getReportsFetchList,
    error: reportsError,
  } = useFetchLegacyReport();

  const {
    isLoading: isSearching,
    data: searchData,
    mutateAsync: searchReports,
    error: searchError,
  } = useSearchLegacyReports();

  useEffect(() => {
    getActorInfo();
  }, [getActorInfo]);

  useEffect(() => {
    setSearch(cleanString(SearchName?.split(" ").join("%20") ?? ""));
  }, [SearchName]);

  useEffect(() => {
    if (actorInfo?.all_companies) {
      getAllCompanies();
    }
    if (
      actorInfo &&
      actorInfo.company_uuids.length > 0 &&
      !actorInfo.all_companies
    ) {
      setCompanyUUID(actorInfo.company_uuids[0]);
    }
  }, [actorInfo, getAllCompanies]);

  const buildParams = useCallback(
    (inputCompanyUUID?: string) => {
      const queryCompanyUUID = inputCompanyUUID || companyUUID;
      if (!queryCompanyUUID) return;
      const extraParams: LegacyReportRequest = {
        company_uuid: queryCompanyUUID,
        limit: pageSize,
      };

      if (filter) {
        extraParams.search = filter;
      }

      return extraParams;
    },
    [filter, companyUUID]
  );

  const onLoadMore = (inputCompanyUUID?: string) => {
    const extraParams = buildParams(inputCompanyUUID);
    if (!extraParams) return;
    if (isSearchActive) {
      const offset = reports.length;
      searchReports({
        company_uuid: extraParams.company_uuid,
        search: extraParams.search || "",
        limit: pageSize,
        offset,
      }).then((response) => {
        setReports((prev) => [
          ...prev,
          ...convertLegacyReportsToRows(response.reports),
        ]);
      });
    } else {
      if (reportsData?.next_token) {
        extraParams.next_token = reportsData.next_token;
      }
      getReportsFetchList(extraParams).then((response) => {
        setReports((prev) => [
          ...prev,
          ...convertLegacyReportsToRows(response.reports),
        ]);
      });
    }
  };

  const handleReportClick = useCallback(
    (report: ReportSummaryItemApi) => {
      const state = { fromCompanyUUID: companyUUID };
      navigate(`/reports/legacy/${report.report_uuid}/summary`, {
        state: state,
      });
    },
    [companyUUID, navigate]
  );

  const convertLegacyReportsToRows = useCallback(
    (
      reports: LegacyReportSummaryItem[] | undefined
    ): ReportRowItem<ReportSummaryItemApi>[] => {
      if (!reports) return [];
      const reportRows: ReportRowItem<ReportSummaryItemApi>[] = reports.map(
        (legacyReport: LegacyReportSummaryItem) => {
          const report: ReportSummaryItemApi = {
            ...legacyReport,
            full_name: `${legacyReport.name}`,
            total_ratings: legacyReport.flagCount,
            report_date_added: new Date(legacyReport.date).toISOString(),
            finish_at: new Date(legacyReport.datesFinished[0]).toISOString(),
            is_locked: legacyReport.paused,
            report_uuid: legacyReport.reportId,
            report_status: legacyReport.personStatus,
            client_report_id: legacyReport.reportId,
            workflow_run_uuid: legacyReport.reportId,
            workflow_status: legacyReport.personStatus,
            has_rerun: legacyReport.numberOfReports > 1,
            created_at: new Date(legacyReport.date).toISOString(),
            due_date: new Date(legacyReport.date).toISOString(),
            due_date_updated: false,
            step_name: "",
            company_name: "",
            company_uuid: "",
            parent_company_name: "",
            parent_company_uuid: "",
            locked_by: "",
            locked_by_name: "",
            person_uuid: "",
            blueprint_uuid: "",
            was_amend: false,
            was_qa: false,
            job_title: "",
            company_note: "",
            blueprint_name: "",
          };
          const data: ReportEntry[] = [
            {
              text: report.full_name,
              style: ReportEntryStyle.COMPONENT,
              component: (
                <div className="flex">
                  <div>
                    <span
                      className="font-semibold underline cursor-pointer"
                      onClick={() => handleReportClick(report)}
                    >
                      {report.full_name}
                    </span>
                  </div>
                </div>
              ),
            },
            {
              text: `${report.total_ratings}`,
              style: ReportEntryStyle.ROOT_COMPONENT,
              component: (
                <div className="flex px-3 items-center flex-1 font-semibold truncate">
                  <ReactTooltip
                    className={"drop-shadow-2xl"}
                    backgroundColor={"#FFFFFF"}
                    id={"flags-count-" + report.report_uuid}
                  >
                    <div className={"flex justify-between w-84 text-black"}>
                      <LegacyDonutChart
                        metrics={legacyReport.webPersonMetrics}
                        showLegend={true}
                      />
                    </div>
                  </ReactTooltip>
                  <div data-for={"flags-count-" + report.report_uuid} data-tip>
                    <LegacyDonutChart
                      metrics={legacyReport.webPersonMetrics}
                      showLegend={false}
                    />
                  </div>
                </div>
              ),
            },
            {
              text: formattedDate(report.report_date_added),
              style: ReportEntryStyle.TEXT,
            },
            {
              text: formattedDate(report.finish_at),
              style: ReportEntryStyle.TEXT,
            },
          ];

          return {
            report: report,
            report_uuid: report.report_uuid,
            isLocked: report.is_locked,
            data: data,
          };
        }
      );
      return reportRows;
    },
    [handleReportClick]
  );

  const initialLoad = useCallback(
    (inputCompanyUUID?: string) => {
      const extraParams = buildParams(inputCompanyUUID);
      if (!extraParams) return;
      getReportsFetchList(extraParams).then((response) => {
        setReports(convertLegacyReportsToRows(response.reports));
      });
    },
    [buildParams, getReportsFetchList, convertLegacyReportsToRows]
  );

  useEffect(() => {
    if (!companyUUID) return;
    if (isSearchActive) {
      searchReports({
        company_uuid: companyUUID,
        search: filter,
        limit: pageSize,
        offset: 0,
      }).then((response) => {
        setReports(convertLegacyReportsToRows(response.reports));
      });
    } else {
      initialLoad(companyUUID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyUUID, filter, initialLoad, isSearchActive]);

  function buildHeader() {
    return ["Name", "Total Flags", "Date Added", "Date Completed"];
  }

  const companyItems =
    companiesInfo?.map((company) => ({
      label: company.name,
      id: company.external_id,
    })) ?? [];

  const view = {
    isLoading: isSearchActive ? isSearching : loadingReports,
    error: isSearchActive ? searchError : reportsError,
    list: reports,
    hasMore: isSearchActive
      ? searchData?.has_more
      : Boolean(reportsData?.next_token),
    total: isSearchActive
      ? searchData?.reports_total ?? 0
      : reportsData?.reports_total ?? 0,
  };

  return (
    <>
      <div className="flex justify-center items-center gap-2">
        {(isLoadingActorInfo || isLoadingCompanies) && !loadingReports && (
          <LoadingSpinner />
        )}
        {actorInfo?.all_companies && !isLoadingCompanies && (
          <div className="mb-4 py-2 w-full">
            <DropdownCombo
              items={companyItems}
              disabled={isLoadingCompanies}
              onChange={(value) => {
                setCompanyUUID(value.id);
              }}
              needTypeAhead={true}
              label={""}
              placeHolder={isLoadingCompanies ? "Loading" : "Select Company"}
              containerClassName="w-full"
              buttonContainerClassName="w-full"
              name="selected-company"
              initial={
                companyItems.find(
                  (item) => item.id === locationState?.fromCompanyUUID
                ) || undefined
              }
            />
          </div>
        )}
      </div>
      {!companyUUID && !isLoadingCompanies && companiesInfo && (
        <div className="text-2xl font-bold text-center py-4">
          Please select a company to view reports
        </div>
      )}
      {view.error && (
        <div className="p-4 rounded-xl bg-red-300 text-red-900 mb-2">
          {genericErrorLoadingMessage}
        </div>
      )}
      <LoadedSince reportData={reportsData} onClear={() => {}} />
      {companyUUID && (
        <>
          {view.isLoading && <LoadingSpinner />}
          {!view.isLoading && (
            <Table
              isLoading={view.isLoading}
              header={buildHeader()}
              data={view.list}
            />
          )}
        </>
      )}
      <div className="flex gap-2 py-2 items-center">
        {!view.isLoading && view.hasMore && (
          <div className="py-2">
            <Button
              text="Load More"
              colorType={ButtonColorTypes.cancel}
              onClick={() => onLoadMore()}
            />
          </div>
        )}
        {companyUUID && !view.isLoading && view.total !== undefined && (
          <div className="text-sm text-gray-500 pl-4">
            {view.list.length === view.total
              ? `${view.total} results found`
              : `${view.list.length} of ${view.total}`}
          </div>
        )}
      </div>
    </>
  );
};

export default ReportsLegacyCoreReport;
